import React from 'react';
import { X, User, Mail, Phone, Shield, Key, Globe, History, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';

export const ViewAdminModal = (props) => {
  const { isViewModalOpen, setIsViewModalOpen, user } = props;
  if (!isViewModalOpen || !user) return null;

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'inactive':
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      case 'suspended':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto'>
      <div className='bg-card rounded-lg shadow-xl w-full max-w-4xl border border-border my-8 max-h-[90vh] overflow-y-auto'>
        {/* Header */}
        <div className='sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-border bg-card'>
          <h3 className='text-lg font-semibold text-foreground'>User Details</h3>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className='hover:bg-muted rounded-md transition-colors'
            type='button'
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          {/* Profile Section */}
          <div className='flex flex-col md:flex-row items-start md:items-center gap-6 mb-6 pb-6 border-b border-border'>
            {/* Profile Photo */}
            <div className='flex-shrink-0'>
              {user.profilePhoto ? (
                <img
                  src={user.profilePhoto}
                  alt={user.name}
                  className='w-24 h-24 rounded-full object-cover border-2 border-border'
                />
              ) : (
                <div className='w-24 h-24 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center'>
                  <User size={40} className='text-primary-foreground' />
                </div>
              )}
            </div>

            {/* Basic Info */}
            <div className='flex-1'>
              <h4 className='text-2xl font-bold text-foreground mb-2'>{user.name}</h4>
              <div className='flex flex-wrap items-center gap-3'>
                <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(user.status)}`}>
                  {user.status}
                </span>
                {user.role?.name && (
                  <span className='px-3 py-1 text-sm font-medium bg-primary/10 text-primary rounded-full border border-primary/20'>
                    {user.role.name}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            {/* Contact Information */}
            <div className='space-y-4'>
              <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider'>Contact Information</h5>

              <div className='space-y-3'>
                <div className='flex items-start gap-3'>
                  <Mail size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Email</p>
                    <p className='text-sm text-foreground break-all'>{user.email}</p>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <Phone size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Phone Number</p>
                    <p className='text-sm text-foreground'>{user.phoneNumber || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Settings */}
            {/* <div className='space-y-4'>
              <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider'>Security</h5>

              <div className='space-y-3'>
                <div className='flex items-start gap-3'>
                  <Shield size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Two-Factor Authentication</p>
                    <div className='flex items-center gap-2 mt-1'>
                      {user.twoFactorAuth?.isEnabled ? (
                        <>
                          <CheckCircle size={16} className='text-green-600 dark:text-green-400' />
                          <span className='text-sm text-green-600 dark:text-green-400'>Enabled</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={16} className='text-muted-foreground' />
                          <span className='text-sm text-muted-foreground'>Disabled</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className='flex items-start gap-3'>
                  <Key size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Last Login</p>
                    <p className='text-sm text-foreground'>{formatDate(user.lastLogin)}</p>
                  </div>
                </div>
              </div>
            </div> */}

            {/* Permissions */}
            {/* {user.role?.permissions && user.role.permissions.length > 0 && (
              <div className='md:col-span-2 space-y-4'>
                <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider'>Permissions</h5>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {user.role.permissions.map((permission) => (
                    <div key={permission._id} className='p-3 bg-muted/50 border border-border rounded-lg'>
                      <p className='text-sm font-medium text-foreground mb-1'>{permission.module}</p>
                      <div className='flex flex-wrap gap-1'>
                        {permission.actions?.map((action, idx) => (
                          <span key={idx} className='px-2 py-0.5 text-xs bg-primary/10 text-primary rounded'>
                            {action}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )} */}

            {/* IP Whitelist */}
            {/* <div className='md:col-span-2 space-y-4'>
              <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2'>
                <Globe size={16} />
                IP Whitelist
              </h5>
              {user.ipWhitelist && user.ipWhitelist.length > 0 ? (
                <div className='flex flex-wrap gap-2'>
                  {user.ipWhitelist.map((ip, idx) => (
                    <span
                      key={idx}
                      className='px-3 py-1.5 text-sm bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-md border border-purple-500/20'
                    >
                      {ip}
                    </span>
                  ))}
                </div>
              ) : (
                <p className='text-sm text-muted-foreground'>No IP restrictions</p>
              )}
            </div> */}

            {/* Login History */}
            {user.loginHistory && user.loginHistory.length > 0 && (
              <div className='md:col-span-2 space-y-4'>
                <h5 className='text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2'>
                  <History size={16} />
                  Recent Login History
                </h5>
                <div className='space-y-2'>
                  {user.loginHistory.slice(0, 5).map((login, idx) => (
                    <div key={idx} className='p-3 bg-muted/30 border border-border rounded-lg'>
                      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
                        <div className='flex items-center gap-3'>
                          <Clock size={16} className='text-muted-foreground flex-shrink-0' />
                          <div>
                            <p className='text-sm text-foreground'>{formatDate(login.timestamp)}</p>
                            <p className='text-xs text-muted-foreground'>{login.ipAddress || 'Unknown IP'}</p>
                          </div>
                        </div>
                        <div className='flex flex-wrap gap-2 text-xs'>
                          {login.device && (
                            <span className='px-2 py-1 bg-muted text-muted-foreground rounded'>{login.device}</span>
                          )}
                          {login.location && (
                            <span className='px-2 py-1 bg-muted text-muted-foreground rounded'>{login.location}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamps */}
            <div className='md:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border'>
              <div className='flex items-start gap-3'>
                <Calendar size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                <div>
                  <p className='text-xs text-muted-foreground'>Created At</p>
                  <p className='text-sm text-foreground'>{formatDate(user.createdAt)}</p>
                </div>
              </div>

              <div className='flex items-start gap-3'>
                <Calendar size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                <div>
                  <p className='text-xs text-muted-foreground'>Updated At</p>
                  <p className='text-sm text-foreground'>{formatDate(user.updatedAt)}</p>
                </div>
              </div>

              {user.createdBy && (
                <div className='flex items-start gap-3'>
                  <User size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Created By</p>
                    <p className='text-sm text-foreground'>{user.createdBy.name}</p>
                  </div>
                </div>
              )}

              {user.updatedBy && (
                <div className='flex items-start gap-3'>
                  <User size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Last Updated By</p>
                    <p className='text-sm text-foreground'>{user.updatedBy.name}</p>
                  </div>
                </div>
              )}

              {user.lastActivity && (
                <div className='flex items-start gap-3'>
                  <Clock size={18} className='text-muted-foreground mt-0.5 flex-shrink-0' />
                  <div>
                    <p className='text-xs text-muted-foreground'>Last Activity</p>
                    <p className='text-sm text-foreground'>{formatDate(user.lastActivity)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='sticky bottom-0 px-6 py-4 border-t border-border bg-card flex justify-end '>
          <button
            onClick={() => setIsViewModalOpen(false)}
            className='px-4 py-2 text-sm border border-border rounded-md hover:bg-accent transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
